import { useQuery } from '@apollo/client';
import { Alert, Box, CircularProgress, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SvbrgConversationEntityVariant } from '@/generated/gql/graphql';
import { ChatThinkingMessage } from '@/legacy/components/Chat/ChatMessage/ChatThinkingMessage';
import { MessageSubmissionContextProvider } from '@/legacy/components/Chat/MessageSubmissionContextProvider/MessageSubmissionContextProvider';
import { useConfig } from '@/legacy/components/ConfigProvider';
import { I18nNamespace } from '~/common/types';
import { ChatConversation } from '~/components/Chat/ChatConversation';
import { EmptyConversation } from '~/components/Conversation/components/EmptyConversation/EmptyConversation';
import { ConversationContextProvider } from '~/components/ConversationContextProvider/ConversationContextProvider';
import {
  getConversationMessageSubmit,
  useConversationContext,
} from '~/components/ConversationContextProvider/ConversationContextProvider.hook';
import { Disclaimer } from '~/components/ConversationInput/components/Disclaimer/Disclaimer';
import { ConversationInput } from '~/components/ConversationInput/ConversationInput';
import { ConversationLayout } from '~/components/ConversationLayout/ConversationLayout';
import { QuickActionsMenu } from '~/components/ConversationQuickAction/components/QuickActionsMenu/QuickActionsMenu';

import { DEMAND_SEQUENCE_NUMBER_QUERY } from './DemandConversation.gql';

interface DemandConversationInnerProps {
  sequenceNumber: string;
  header?: React.ReactNode;
}

const InnerDemandConversation = ({ sequenceNumber, header }: DemandConversationInnerProps) => {
  const { t } = useTranslation(I18nNamespace.conversation, { keyPrefix: 'DemandConversation' });
  const { features } = useConfig();

  const { status, messages, error, inputProps, sendMessage } = useConversationContext();
  const onSubmit = getConversationMessageSubmit({ sendMessage });

  return (
    <MessageSubmissionContextProvider sendMessage={sendMessage}>
      <Stack flex={1} overflow="auto">
        {header}
        <ConversationLayout
          input={
            <ConversationInput
              enableAttachments
              {...inputProps}
              forceAutofocus
              onSubmit={onSubmit}
              actions={
                <QuickActionsMenu disabled={inputProps.disabled || !features.showDemandConversationQuickActions} />
              }
            />
          }
          disclaimer={<Disclaimer />}
        >
          {status === 'initialized' ? (
            <EmptyConversation
              entity={t('entity', { sequenceNumber })}
              showQuickActions={features.showDemandConversationQuickActions}
            />
          ) : status === 'loading' ? (
            <Box display="flex" flex={1} justifyContent="center">
              <CircularProgress sx={{ alignSelf: 'center', justifySelf: 'center' }} />
            </Box>
          ) : (
            <ChatConversation messages={messages} status={status}>
              {error && <Alert severity="error">{error.message}</Alert>}
              {status === 'submitted' && <ChatThinkingMessage />}
            </ChatConversation>
          )}
        </ConversationLayout>
      </Stack>
    </MessageSubmissionContextProvider>
  );
};

interface DemandConversationProps {
  demandId: string;
  sequenceNumber?: string;
  header?: React.ReactNode;
  skip?: boolean;
}

export const DemandConversation = ({ demandId, sequenceNumber, header, skip }: DemandConversationProps) => {
  const { t } = useTranslation(I18nNamespace.conversation, { keyPrefix: 'DemandConversation' });

  const { data, loading, error } = useQuery(DEMAND_SEQUENCE_NUMBER_QUERY, {
    variables: { id: demandId },
    skip: !!sequenceNumber || skip,
  });

  const demandSequenceNumber = sequenceNumber || data?.tesseract.demand?.sequenceNumber;

  if (loading) {
    return (
      <Box display="flex" flex={1} justifyContent="center">
        <CircularProgress sx={{ alignSelf: 'center', justifySelf: 'center' }} />
      </Box>
    );
  }

  if (error || !demandSequenceNumber) {
    return <Alert severity="error">{t('error.loading')}</Alert>;
  }

  return demandId ? (
    <ConversationContextProvider
      entityId={demandId}
      entityVariant={SvbrgConversationEntityVariant.DEMAND}
      title={t('entity', { sequenceNumber })}
      skip={skip}
    >
      {skip ? null : <InnerDemandConversation sequenceNumber={demandSequenceNumber} header={header} />}
    </ConversationContextProvider>
  ) : null;
};
